import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Collapse, IconButton, TextField, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Form, Formik } from 'formik'
import ReactDatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { isAxiosError } from 'lib/axios'
import { CloseChipIcon, InfoTooltipIcon } from 'ui/components/svg'
import { ThemeButton } from 'ui/components/buttons'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import ControlledTextField from 'ui/components/form-components/ControlledTextField'
import SelectV2 from 'ui/components/form-components-v2/select-v2/SelectV2'
import { DEFAULT_MENU_PROPS } from 'ui/components/form-components-v2/select-v2/selectV2.configs'
import { HotToast } from 'ui/components/toast'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
  CreateExclusionRulePayload,
  DEVICES_COLUMN_NAME,
  ExclusionField,
  ExclusionRuleMatchType,
  ExclusionRuleScopeType,
  MAX_CONDITION_VALUES
} from 'models'
import { ExclusionRulesServiceV4 } from 'services'
import {
  AddConditionButton,
  ConditionRow,
  ConditionsPanel,
  ModalActions,
  ModalBody,
  ModalHeader,
  ModalRoot
} from './createExclusionRule.styles'
import { showExclusionRuleCreatedToast } from './showExclusionRuleCreatedToast'
import {
  ConditionProblem,
  conditionProblem,
  DraftCondition,
  draftFromConditionInput,
  findField,
  makeDraftCondition,
  toConditionInputs,
  valueShapeOf,
  withField,
  withOperator
} from './conditionBuilder'
import { NO_EDIT_PERMISSION_MESSAGE, RULE_CONFLICT_MESSAGE, RULE_GONE_MESSAGE } from './exclusionRule.constants'

interface FormValues {
  rule_name: string
  reason: string
}

interface LookupState {
  status: 'loading' | 'ready' | 'failed'
  values: string[]
}

const ruleSchema = Yup.object({
  rule_name: Yup.string().trim().required('Exclusion rule name is required'),
  reason: Yup.string()
})

const MATCH_OPTIONS = [
  { label: 'All', value: ExclusionRuleMatchType.All },
  { label: 'Any', value: ExclusionRuleMatchType.Any }
]

const MATCH_HINT = 'Any - matches at least one condition; All - matches every condition.'

// The labels sit on the border from the start, as in the design, rather than inside an empty field.
const FLOATING_LABEL = { shrink: true }

// The app modal sits at z-index 10002, so its menus must render above it.
const MENU_PROPS = { ...DEFAULT_MENU_PROPS, sx: { zIndex: 10003 } }

const DATE_VALUE_FORMAT = 'YYYY-MM-DD'

// A record-identifier lookup answers with one value per record, far more than a menu can render.
const VALUE_OPTIONS_LIMIT = 200

const BOOLEAN_OPTIONS = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' }
]

const PROBLEM_MESSAGES: Partial<Record<ConditionProblem, string>> = {
  range: 'The first value must not be greater than the second.',
  tooManyValues: `A condition can hold up to ${MAX_CONDITION_VALUES.toLocaleString('en-US')} values.`
}

const errorMessage = (e: unknown): string | null => {
  if (!isAxiosError(e)) return null
  const data = e.response?.data as { detail?: unknown; message?: unknown } | undefined
  const text = data?.detail ?? data?.message
  return typeof text === 'string' ? text : null
}

const TrashIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.25 4.5h13.5M6.75 4.5V3.375C6.75 2.754 7.254 2.25 7.875 2.25h2.25c.621 0 1.125.504 1.125 1.125V4.5m1.875 0v9.75c0 .621-.504 1.125-1.125 1.125H5.25c-.621 0-1.125-.504-1.125-1.125V4.5h9.75Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const renderMultiValue = (labelOf: (value: string) => string) => (selected: unknown) => {
  const values = Array.isArray(selected) ? (selected as string[]) : []
  if (!values.length) return 'Select'
  if (values.length === 1) return labelOf(values[0])
  return (
    <span className="multi-value">
      <span className="multi-value__first">{labelOf(values[0])},</span>
      <span className="multi-value__more">+{values.length - 1}</span>
    </span>
  )
}

// Some string columns store a JSON array as text (e.g. CVE: '["CVE-1","CVE-2"]'); show it as plain text.
const plainText = (value: string): string => {
  if (!value.startsWith('[')) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.join(', ') : value
  } catch {
    return value
  }
}

// Array columns answer the lookup with each record's whole array as a JSON string; offer the elements instead.
const lookupValuesOf = (field: ExclusionField, raw: unknown): string[] => {
  const values = (Array.isArray(raw) ? raw : []).filter((v) => v != null).map(String)
  if (field.type !== 'array') return values
  const elements = values.flatMap((v) => {
    try {
      const parsed = JSON.parse(v)
      return Array.isArray(parsed) ? parsed.filter((e) => e != null).map(String) : [v]
    } catch {
      return [v]
    }
  })
  return Array.from(new Set(elements))
}

interface SingleValueInputProps {
  field: ExclusionField
  id: string
  value: string
  placeholder: string
  onChange: (next: string) => void
}

const SingleValueInput: FC<SingleValueInputProps> = ({ field, id, value, placeholder, onChange }) => {
  if (field.type === 'boolean') {
    return (
      <SelectV2
        MenuProps={MENU_PROPS}
        id={id}
        label=""
        placeholder="Select"
        value={value}
        options={BOOLEAN_OPTIONS}
        onChange={(e) => onChange(String(e.target.value))}
      />
    )
  }
  if (field.type === 'timestamp') {
    const selected = value && dayjs(value).isValid() ? dayjs(value).toDate() : null
    // Same MM/DD/YY picker as the record-page date filter; the stored value stays YYYY-MM-DD.
    return (
      <ReactDatePicker
        id={id}
        selected={selected}
        onChange={(date: Date | null) => onChange(date ? dayjs(date).format(DATE_VALUE_FORMAT) : '')}
        dateFormat="MM/dd/yy"
        placeholderText="MM/DD/YY"
        formatWeekDay={(day) => day.charAt(0)}
        popperClassName="ag-custom-component-popup"
        popperPlacement="bottom-start"
        // Fixed positioning keeps the calendar from being clipped by the scrolling modal body.
        popperProps={{ strategy: 'fixed' }}
        wrapperClassName="date-input"
        autoComplete="off"
        customInput={<TextField fullWidth size="small" />}
      />
    )
  }
  return (
    <TextField
      fullWidth
      size="small"
      type={field.type === 'number' ? 'number' : 'text'}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

const CreateExclusionRule: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const props = modalProps.props?.createExclusionRuleProps
  const fields = useMemo(() => props?.exclusionFields ?? [], [props])

  const isEdit = props?.mode === 'edit'
  const ruleId = props?.rule?.id

  const [previewName, setPreviewName] = useState('')
  const [previewLoading, setPreviewLoading] = useState(!isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  // The modal remounts on every open, so a lazy initializer seeds the rows once.
  const [conditions, setConditions] = useState<DraftCondition[]>(() => {
    if (props?.initialConditions.length) return props.initialConditions.map(draftFromConditionInput)
    return [makeDraftCondition()]
  })
  // Values each prefilled row started with; the value picker lists them as their own group.
  const [initialValues, setInitialValues] = useState<Record<string, { field: string; values: string[] }>>(() =>
    Object.fromEntries(conditions.map((c) => [c.key, { field: c.field_name, values: c.values }]))
  )
  const [matchType, setMatchType] = useState<ExclusionRuleMatchType>(
    props?.initialMatchType ?? ExclusionRuleMatchType.All
  )
  const [stored, setStored] = useState<{ version: number; rule_name: string; reason: string } | null>(null)
  const [ruleLoading, setRuleLoading] = useState(isEdit)
  const [conflict, setConflict] = useState(false)
  const [lookups, setLookups] = useState<Record<string, LookupState>>({})
  const [conditionsOpen, setConditionsOpen] = useState(true)
  const requestedLookups = useRef(new Set<string>())

  const fieldOptions = useMemo(() => fields.map((f) => ({ label: f.label, value: f.name })), [fields])

  const fallbackName = useMemo(() => {
    if (!props) return ''
    const count = props.mode === 'bulk' ? props.initialConditions[0]?.predicate_value.values.length ?? 0 : 0
    return count > 1
      ? `${props.cpiDisplayId} Exclusion Rule (${count} ${props.unitsLabel})`
      : `${props.cpiDisplayId} Exclusion Rule`
  }, [props])

  const ruleGone = useCallback(() => {
    toast.error(() => <HotToast text={RULE_GONE_MESSAGE} />)
    props?.onSuccess?.()
    dispatch(closeModal())
  }, [props, dispatch])

  // Edits always start from the rule as it is now, not from the grid row.
  const loadRule = useCallback(async () => {
    if (!ruleId) return
    setRuleLoading(true)
    try {
      const { data } = await ExclusionRulesServiceV4.getRule(ruleId)
      const drafts = data.conditions.filter((c) => !c.deleted_at).map(draftFromConditionInput)
      setConditions(drafts)
      setInitialValues(Object.fromEntries(drafts.map((c) => [c.key, { field: c.field_name, values: c.values }])))
      setMatchType(data.match_type ?? ExclusionRuleMatchType.All)
      setStored({ version: data.version, rule_name: data.rule_name, reason: data.reason ?? '' })
      setConflict(false)
      setServerError(null)
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        ruleGone()
        return
      }
      console.error(e)
      toast.error(() => <HotToast text="Failed to load exclusion rule. Please try again." />)
      dispatch(closeModal())
    } finally {
      setRuleLoading(false)
    }
  }, [ruleId, ruleGone, dispatch])

  useEffect(() => {
    if (isEdit) loadRule()
  }, [isEdit, loadRule])

  // A rejected save explains itself inside the conditions, so a collapsed card must open to show it.
  useEffect(() => {
    if (serverError) setConditionsOpen(true)
  }, [serverError])

  useEffect(() => {
    if (!props || isEdit) return
    let cancelled = false
    setPreviewLoading(true)
    ExclusionRulesServiceV4.getPreviewName(ExclusionRuleScopeType.Metric, props.cpiSlug)
      .then((res) => {
        if (!cancelled) setPreviewName((typeof res.data === 'string' && res.data) || fallbackName)
      })
      .catch(() => {
        if (!cancelled) setPreviewName(fallbackName)
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [props, isEdit, fallbackName])

  // The lookup is only asked for multi-value operators, and only once per field.
  useEffect(() => {
    if (!props) return
    conditions.forEach((c) => {
      const field = findField(fields, c.field_name)
      if (!field || valueShapeOf(field, c.predicate_type) !== 'one_or_more') return
      if (requestedLookups.current.has(field.name)) return
      requestedLookups.current.add(field.name)
      setLookups((prev) => ({ ...prev, [field.name]: { status: 'loading', values: [] } }))
      ExclusionRulesServiceV4.getFieldValues(props.cpiSlug, field.name)
        .then((res) => {
          const values = lookupValuesOf(field, res.data)
          setLookups((prev) => ({ ...prev, [field.name]: { status: 'ready', values } }))
        })
        .catch(() => setLookups((prev) => ({ ...prev, [field.name]: { status: 'failed', values: [] } })))
    })
  }, [conditions, fields, props])

  const problems = useMemo(
    () => Object.fromEntries(conditions.map((c) => [c.key, conditionProblem(c, fields)])),
    [conditions, fields]
  )
  const conditionsValid = conditions.length > 0 && conditions.every((c) => problems[c.key] === null)

  const updateCondition = (key: string, update: (c: DraftCondition) => DraftCondition) => {
    setServerError(null)
    setConditions((prev) => prev.map((c) => (c.key === key ? update(c) : c)))
  }

  const handleFieldChange = (key: string, fieldName: string) => {
    const field = findField(fields, fieldName)
    if (field) updateCondition(key, (c) => withField(c, field))
  }

  const addCondition = () => {
    setServerError(null)
    setConditions((prev) => [...prev, makeDraftCondition()])
  }

  const removeCondition = (key: string) => {
    setServerError(null)
    setConditions((prev) => prev.filter((c) => c.key !== key))
  }

  const handleClose = () => {
    if (submitting) return
    dispatch(closeModal())
  }

  const saveRule = async (values: FormValues) => {
    if (!props) return
    const common = {
      rule_name: values.rule_name.trim(),
      reason: values.reason.trim() || null,
      match_type: matchType,
      conditions: toConditionInputs(conditions, fields)
    }
    if (isEdit) {
      if (!ruleId || !stored) return
      await ExclusionRulesServiceV4.updateRule(ruleId, { version: stored.version, ...common })
      return
    }
    const payload: CreateExclusionRulePayload = {
      scope_type: ExclusionRuleScopeType.Metric,
      scope_id: props.cpiSlug,
      ...common
    }
    await ExclusionRulesServiceV4.createRule(payload)
  }

  const handleSubmit = async (values: FormValues) => {
    if (!props || !conditionsValid || conflict) return
    setSubmitting(true)
    setServerError(null)
    try {
      await saveRule(values)
      showExclusionRuleCreatedToast()
      props.onSuccess?.()
      dispatch(closeModal())
    } catch (e) {
      const status = isAxiosError(e) ? e.response?.status : undefined
      if (isEdit && status === 409) {
        setConflict(true)
        setServerError(RULE_CONFLICT_MESSAGE)
      } else if (isEdit && status === 404) {
        ruleGone()
      } else if (status === 400 || status === 422) {
        setServerError(errorMessage(e) || 'This rule could not be saved. Check the conditions and try again.')
      } else if (status === 403) {
        toast.error(() => <HotToast text={NO_EDIT_PERMISSION_MESSAGE} />, { id: 'exclusion-rule-forbidden' })
        dispatch(closeModal())
      } else {
        console.error(e)
        toast.error(() => (
          <HotToast text={`Failed to ${isEdit ? 'save' : 'create'} exclusion rule. Please try again.`} />
        ))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!props) return null

  const prefixLabel = matchType === ExclusionRuleMatchType.All ? 'And' : 'Or'

  // Labels only: the rule must still carry the raw value (instance_name, the stored JSON text) to match records.
  const valueLabelOf = (field: ExclusionField) => (value: string) =>
    field.name === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
      ? props.instanceDisplayNames?.[value] ?? value
      : plainText(value)

  const valuePickerOptions = (c: DraftCondition, field: ExclusionField) => {
    const initial = initialValues[c.key]
    const excluded = initial?.field === field.name ? initial.values : []
    const lookup = lookups[field.name]
    const others = (lookup?.values ?? []).filter((v) => !excluded.includes(v))
    const labelOf = valueLabelOf(field)
    const toOption = (v: string) => ({ label: labelOf(v), value: v })
    // Headings only earn their place when there are two groups to tell apart.
    if (!excluded.length) return others.map(toOption)
    return [
      { groupHeader: true, label: 'Excluded values' },
      ...excluded.map(toOption),
      ...(others.length ? [{ groupHeader: true, label: 'Other values' }, ...others.map(toOption)] : [])
    ]
  }

  const renderValueCell = (c: DraftCondition, field: ExclusionField) => {
    const shape = valueShapeOf(field, c.predicate_type)
    if (shape === 'one_or_more') {
      const lookup = lookups[field.name]
      return (
        <SelectV2
          MenuProps={MENU_PROPS}
          id={`value-${c.key}`}
          label=""
          placeholder="Select"
          multiple
          searchable
          maxVisibleOptions={VALUE_OPTIONS_LIMIT}
          value={c.values}
          options={valuePickerOptions(c, field)}
          emptyMessage={lookup?.status === 'loading' ? 'Loading values...' : 'No values available'}
          renderValue={renderMultiValue(valueLabelOf(field))}
          onChange={(e) => {
            const next = e.target.value
            updateCondition(c.key, (cur) => ({ ...cur, values: Array.isArray(next) ? next.map(String) : [] }))
          }}
        />
      )
    }
    if (shape === 'exactly_one') {
      return (
        <SingleValueInput
          id={`value-${c.key}`}
          field={field}
          value={c.values[0] ?? ''}
          placeholder="Value"
          onChange={(v) => updateCondition(c.key, (cur) => ({ ...cur, values: [v] }))}
        />
      )
    }
    if (shape === 'exactly_two') {
      const setAt = (index: number, v: string) =>
        updateCondition(c.key, (cur) => {
          const next = [cur.values[0] ?? '', cur.values[1] ?? '']
          next[index] = v
          return { ...cur, values: next }
        })
      return (
        <Box className="between-cell">
          <SingleValueInput
            id={`low-${c.key}`}
            field={field}
            value={c.values[0] ?? ''}
            placeholder="From"
            onChange={(v) => setAt(0, v)}
          />
          <SingleValueInput
            id={`high-${c.key}`}
            field={field}
            value={c.values[1] ?? ''}
            placeholder="To"
            onChange={(v) => setAt(1, v)}
          />
        </Box>
      )
    }
    return null
  }

  const renderConditionRow = (c: DraftCondition, idx: number) => {
    const field = findField(fields, c.field_name)
    const problem = problems[c.key]
    const rowMessage = problem ? PROBLEM_MESSAGES[problem] : undefined
    const flagged = Boolean(serverError && c.field_name && serverError.includes(c.field_name))
    // A stored condition whose field is no longer excludable has no label or type: show it as-is.
    const readOnly = Boolean(c.field_name && !field)

    return (
      <Box key={c.key} className={flagged ? 'condition flagged' : 'condition'}>
        <ConditionRow>
          <span className="prefix">{idx === 0 ? '' : prefixLabel}</span>
          {readOnly ? (
            <>
              <TextField fullWidth size="small" disabled value={c.field_name} />
              <TextField fullWidth size="small" disabled value={c.predicate_type} />
              <Box className="value-cell">
                <TextField fullWidth size="small" disabled value={c.values.join(', ')} />
              </Box>
            </>
          ) : (
            <>
              <SelectV2
                MenuProps={MENU_PROPS}
                id={`attr-${c.key}`}
                label=""
                placeholder="Select"
                searchable
                value={c.field_name}
                options={fieldOptions}
                onChange={(e) => handleFieldChange(c.key, String(e.target.value))}
              />
              <SelectV2
                MenuProps={MENU_PROPS}
                id={`op-${c.key}`}
                label=""
                placeholder="Select"
                value={c.predicate_type}
                disabled={!field}
                options={(field?.operators ?? []).map((o) => ({ label: o.label, value: o.predicate }))}
                onChange={(e) => updateCondition(c.key, (cur) => withOperator(cur, String(e.target.value)))}
              />
              <Box className="value-cell">
                {field ? (
                  renderValueCell(c, field)
                ) : (
                  <TextField fullWidth size="small" disabled placeholder="Enter value..." />
                )}
              </Box>
            </>
          )}
          <IconButton
            className="delete-btn"
            aria-label="Remove condition"
            disabled={conditions.length === 1}
            onClick={() => removeCondition(c.key)}
          >
            <TrashIcon />
          </IconButton>
        </ConditionRow>
        {rowMessage && <Typography className="condition-error">{rowMessage}</Typography>}
      </Box>
    )
  }

  return (
    <ModalRoot>
      <ModalHeader>
        <Typography className="title">{isEdit ? 'Edit Exclusion Rule' : 'Create Exclusion Rule'}</Typography>
        <IconButton className="close-button" onClick={handleClose} disabled={submitting}>
          <CloseChipIcon />
        </IconButton>
      </ModalHeader>

      <Formik
        enableReinitialize
        initialValues={
          isEdit
            ? { rule_name: stored?.rule_name ?? '', reason: stored?.reason ?? '' }
            : { rule_name: previewName, reason: '' }
        }
        validationSchema={ruleSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, values, errors, touched, setFieldValue, handleBlur }) => (
          <Form>
            <ModalBody>
              <Typography className="explanation">
                Exclusion Rule removes records from related metrics and calculations.
              </Typography>

              {previewLoading || ruleLoading ? (
                <TextField
                  fullWidth
                  disabled
                  required
                  label="Exclusion Rules"
                  InputLabelProps={FLOATING_LABEL}
                  value={ruleLoading ? 'Loading...' : 'Generating name...'}
                />
              ) : (
                <ControlledTextField
                  name="rule_name"
                  fullWidth
                  required
                  label="Exclusion Rules"
                  InputLabelProps={FLOATING_LABEL}
                  autoComplete="off"
                  error={Boolean(errors.rule_name && touched.rule_name)}
                  helperText={touched.rule_name ? errors.rule_name : undefined}
                />
              )}

              <TextField
                name="reason"
                fullWidth
                multiline
                minRows={3}
                label="Reason for Exclusion"
                InputLabelProps={FLOATING_LABEL}
                placeholder="Enter a reason..."
                value={values.reason}
                onChange={(e) => setFieldValue('reason', e.target.value)}
                onBlur={handleBlur}
              />

              <ConditionsPanel>
                <Box className="conditions-header">
                  <Box className="conditions-title-group">
                    <Typography className="conditions-title">Conditions</Typography>
                    <OnyxiaTooltip
                      title={MATCH_HINT}
                      placement="top-start"
                      arrow={false}
                      PopperProps={{ sx: { zIndex: 10003 } }}
                    >
                      <span className="conditions-hint" aria-label={MATCH_HINT}>
                        <InfoTooltipIcon />
                      </span>
                    </OnyxiaTooltip>
                  </Box>
                  <Box className="conditions-header-actions">
                    {conditions.length > 1 && (
                      <Box className="match-control">
                        <span>Match</span>
                        <Box className="match-select">
                          <SelectV2
                            MenuProps={MENU_PROPS}
                            id="match-type"
                            label=""
                            value={matchType}
                            options={MATCH_OPTIONS}
                            onChange={(e) => setMatchType(e.target.value as ExclusionRuleMatchType)}
                          />
                        </Box>
                        <span>conditions</span>
                      </Box>
                    )}
                    <IconButton
                      className={conditionsOpen ? 'conditions-toggle open' : 'conditions-toggle'}
                      aria-label={conditionsOpen ? 'Collapse conditions' : 'Expand conditions'}
                      aria-expanded={conditionsOpen}
                      onClick={() => setConditionsOpen((open) => !open)}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={conditionsOpen}>
                  <Box className="conditions-body">
                    {!ruleLoading && conditions.map(renderConditionRow)}

                    {serverError && (
                      <Box className="server-error-row">
                        <Typography className="server-error">{serverError}</Typography>
                        {conflict && (
                          <AddConditionButton type="button" onClick={loadRule}>
                            Reload
                          </AddConditionButton>
                        )}
                      </Box>
                    )}

                    <AddConditionButton type="button" onClick={addCondition}>
                      Add Condition
                    </AddConditionButton>
                  </Box>
                </Collapse>
              </ConditionsPanel>
            </ModalBody>

            <ModalActions>
              <ThemeButton
                themevariant="secondary"
                shape="rectangular"
                type="button"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </ThemeButton>
              <ThemeButton
                themevariant="primary"
                shape="rectangular"
                type="submit"
                disabled={!isValid || !conditionsValid || submitting || previewLoading || ruleLoading || conflict}
              >
                {isEdit ? 'Save' : 'Create'}
              </ThemeButton>
            </ModalActions>
          </Form>
        )}
      </Formik>
    </ModalRoot>
  )
}

export default CreateExclusionRule
